import { type ReactNode, createElement, Fragment } from "react";

/**
 * Tiny markdown renderer ported from the mockup: headings (#..###), unordered
 * lists, bold, italic, inline code, and links. Renders straight to React nodes
 * (no dangerouslySetInnerHTML), so escaping is handled by React itself.
 */

let keySeq = 0;
function key(): string {
  return `md-${keySeq++}`;
}

/** Parse inline spans: `code`, **bold**, *italic*, [text](url). */
function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  const pattern = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(\[[^\]]+\]\(https?:[^)\s]+\))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("`")) {
      nodes.push(createElement("code", { key: key() }, tok.slice(1, -1)));
    } else if (tok.startsWith("**")) {
      nodes.push(createElement("strong", { key: key() }, tok.slice(2, -2)));
    } else if (tok.startsWith("*")) {
      nodes.push(createElement("em", { key: key() }, tok.slice(1, -1)));
    } else {
      const linkMatch = /^\[([^\]]+)\]\((https?:[^)\s]+)\)$/.exec(tok);
      if (linkMatch) {
        nodes.push(
          createElement(
            "a",
            { key: key(), href: linkMatch[2], target: "_blank", rel: "noopener" },
            linkMatch[1],
          ),
        );
      } else {
        nodes.push(tok);
      }
    }
    last = m.index + tok.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

/** Render a markdown string to block-level React nodes. */
export function renderMarkdown(md: string): ReactNode {
  const lines = md.replace(/\r\n/g, "\n").split("\n");
  const blocks: ReactNode[] = [];
  let para: string[] = [];
  let list: string[] = [];

  const flushPara = () => {
    if (para.length) {
      blocks.push(createElement("p", { key: key() }, renderInline(para.join(" "))));
      para = [];
    }
  };
  const flushList = () => {
    if (list.length) {
      const items = list.map((li) => createElement("li", { key: key() }, renderInline(li)));
      blocks.push(createElement("ul", { key: key() }, items));
      list = [];
    }
  };

  for (const line of lines) {
    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    const li = /^[-*]\s+(.*)$/.exec(line);
    if (h) {
      flushPara();
      flushList();
      const lvl = h[1]!.length;
      blocks.push(createElement(`h${lvl}`, { key: key() }, renderInline(h[2]!)));
    } else if (li) {
      flushPara();
      list.push(li[1]!);
    } else if (line.trim() === "") {
      flushPara();
      flushList();
    } else {
      flushList();
      para.push(line.trim());
    }
  }
  flushPara();
  flushList();

  return createElement(Fragment, null, blocks);
}
